import { AI_BASE_URL } from "../config";

/**
 * Generic API request function
 * @param {string} endpoint - API endpoint
 * @param {string} method - HTTP method
 * @param {Object} data - Request body data
 * @returns {Promise} - API response
 */
const apiRequest = async (endpoint, method = "GET", data = null) => {
  const url = `${AI_BASE_URL}${endpoint}`;

  const headers = {
    "Content-Type": "application/json",
  };

  // Get auth token from localStorage if it exists
  const token = localStorage.getItem("auth_token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const options = {
    method,
    headers,
    credentials: "include",
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, options);

    // Log response status

    // Handle non-2xx responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `API Error: ${response.status}`);
    }

    // Parse JSON response, or return empty object if no content
    const result = await response.json().catch(() => ({}));
    return result;
  } catch (error) {
    throw error;
  }
};

// Chat versions API functions
export const chatVersionsApi = {
  // Save a new chat version - reviewId is the primary identifier
  saveVersion: (chatId, content, title, reviewId = null) => {
    if (!reviewId) {
      return Promise.reject(new Error("Review ID is required"));
    }

    // If chatId is not provided, use reviewId as chatId
    const effectiveChatId = chatId || reviewId;

    return apiRequest("/chat-versions", "POST", {
      chat_id: effectiveChatId,
      content,
      title,
      review_id: reviewId,
    });
  },

  // Get all versions for a chat
  getVersions: (chatId) => {
    if (!chatId) {
      return Promise.reject(new Error("Chat ID is required"));
    }

    return apiRequest(`/chat-versions/${chatId}`);
  },

  // Get all versions for a review - this is the primary method
  getVersionsByReview: (reviewId) => {
    if (!reviewId) {
      return Promise.reject(new Error("Review ID is required"));
    }

    // Check if the endpoint exists before making the request
    return apiRequest(`/chat-versions/review/${reviewId}`).catch(error => {
      // If the endpoint doesn't exist (404), return empty array instead of throwing
      if (error.message.includes('404') || error.message.includes('No route found')) {
        return [];
      }
      throw error;
    });
  },

  // Get the latest version for a chat
  getLatestVersion: (chatId) => {
    if (!chatId) {
      return Promise.reject(new Error("Chat ID is required"));
    }

    return apiRequest(`/chat-versions/${chatId}/latest`);
  },

  // Get the latest version for a review - this is the primary method
  getLatestVersionByReview: (reviewId) => {
    if (!reviewId) {
      return Promise.reject(new Error("Review ID is required"));
    }

    return apiRequest(`/chat-versions/review/${reviewId}/latest`).catch(error => {
      // If the endpoint doesn't exist (404), return null instead of throwing
      if (error.message.includes('404') || error.message.includes('No route found')) {
        return null;
      }
      throw error;
    });
  },

  // Get a specific version for a chat
  getVersion: (chatId, versionNumber) => {
    if (!chatId) {
      return Promise.reject(new Error("Chat ID is required"));
    }

    return apiRequest(`/chat-versions/${chatId}/${versionNumber}`);
  },

  // Get a specific version for a review - this is the primary method
  getVersionByReview: (reviewId, versionNumber) => {
    if (!reviewId) {
      return Promise.reject(new Error("Review ID is required"));
    }

    return apiRequest(`/chat-versions/review/${reviewId}/${versionNumber}`).catch(error => {
      // If the endpoint doesn't exist (404), return null instead of throwing
      if (error.message.includes('404') || error.message.includes('No route found')) {
        return null;
      }
      throw error;
    });
  },

  // Link existing versions to a review
  linkVersionsToReview: (chatId, reviewId) => {
    if (!chatId || !reviewId) {
      return Promise.reject(
        new Error("Both Chat ID and Review ID are required")
      );
    }

    return apiRequest("/chat-versions/link-review", "PUT", {
      chat_id: chatId,
      review_id: reviewId,
    });
  },
};

export default {
  chatVersions: chatVersionsApi,
};
